// Pure migration logic for moving `challenge` cards out of default lessons and into a
// practice lesson's task list. Kept free of React and antd imports so it is trivially
// unit-testable, matching the convention in components/reorder.ts.
import { Lesson, PracticeTask } from '../../api/types';

export interface SourceTask {
  /** Stable selection key: `${lessonId}:${cardIndex}`. */
  key: string;
  lessonId: number;
  lessonTitle: string;
  cardIndex: number;
  title: string;
  text: string;
  tip: string | null;
}

export interface SourceGroup {
  lessonId: number;
  lessonTitle: string;
  tasks: SourceTask[];
}

export interface StagedTask {
  task: PracticeTask;
  /** null for a task typed directly into the practice lesson rather than migrated. */
  origin: SourceTask | null;
  keepInSource: boolean;
}

function cardsOf(lesson: Lesson): any[] {
  return Array.isArray(lesson.body?.cards) ? lesson.body.cards : [];
}

/**
 * Walk every default lesson's cards and collect the `challenge` entries, grouped under
 * their source lesson and ordered by the lesson's sortOrder. Lessons contributing no
 * challenge cards are omitted entirely.
 *
 * `Deleted` lessons are skipped. They are soft-deleted content — LessonsPage hides them
 * behind a filter that defaults to ['Enabled', 'Disabled'] — so offering their cards would
 * both resurrect dead content as live tasks and make Apply write to a row the admin cannot
 * see. `Disabled` lessons stay in scope: a work-in-progress or not-yet-published lesson is
 * real content whose tasks are exactly what an admin wants to sweep up.
 */
export function extractChallengeCards(lessons: Lesson[]): SourceGroup[] {
  return lessons
    .filter((l) => l.type !== 'Practice' && l.status !== 'Deleted')
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((lesson) => ({
      lessonId: lesson.id,
      lessonTitle: lesson.title,
      tasks: cardsOf(lesson).reduce<SourceTask[]>((acc, card, cardIndex) => {
        if (!card || card.type !== 'challenge') return acc;
        acc.push({
          key: `${lesson.id}:${cardIndex}`,
          lessonId: lesson.id,
          lessonTitle: lesson.title,
          cardIndex,
          title: card.title ?? '',
          text: card.challengeText ?? '',
          tip: card.challengeTip ?? null,
        });
        return acc;
      }, []),
    }))
    .filter((group) => group.tasks.length > 0);
}

/**
 * Produce the exact batch-update-bodies payload: the practice lesson first (additive), then
 * one entry per source lesson that actually loses a card. Ordering matters only for
 * readability — the server applies the whole batch in one transaction.
 */
export function buildMigrationPlan(
  practiceLessonId: number,
  staged: StagedTask[],
  sources: Lesson[],
): { id: number; body: unknown }[] {
  const plan: { id: number; body: unknown }[] = [
    { id: practiceLessonId, body: { tasks: staged.map((s) => s.task) } },
  ];

  const removalsByLesson = new Map<number, Set<number>>();
  for (const entry of staged) {
    if (!entry.origin || entry.keepInSource) continue;
    const set = removalsByLesson.get(entry.origin.lessonId) ?? new Set<number>();
    set.add(entry.origin.cardIndex);
    removalsByLesson.set(entry.origin.lessonId, set);
  }

  for (const lesson of sources) {
    const removals = removalsByLesson.get(lesson.id);
    if (!removals || removals.size === 0) continue;
    plan.push({
      id: lesson.id,
      body: { ...lesson.body, cards: cardsOf(lesson).filter((_, i) => !removals.has(i)) },
    });
  }

  return plan;
}
